import {
  useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { getConfig } from '@edx/frontend-platform';
import { useIntl } from '@edx/frontend-platform/i18n';

import messages from './messages';

export const IFRAME_FEATURE_POLICY = 'microphone *; camera *; midi *; geolocation *; encrypted-media *; clipboard-write *';
const MIN_IFRAME_HEIGHT = 200;
const INITIAL_IFRAME_HEIGHT = 600;
const RESIZE_PADDING = 10;

const XBlockContainerIframe = ({ blockId, xblockCount }) => {
  const intl = useIntl();
  const iframeRef = useRef(null);
  const [iframeHeight, setIframeHeight] = useState(INITIAL_IFRAME_HEIGHT);

  const iframeUrl = useMemo(
    () => `${getConfig().STUDIO_BASE_URL}/container_embed/${blockId}`,
    [blockId],
  );

  useEffect(() => {
    setIframeHeight(INITIAL_IFRAME_HEIGHT);
  }, [iframeUrl]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) {
        return;
      }

      const { data } = event;

      if (data?.type === 'plugin.resize') {
        const nextHeight = Number(data.payload?.height);
        if (Number.isFinite(nextHeight) && nextHeight > 0) {
          setIframeHeight(Math.max(nextHeight + RESIZE_PADDING, MIN_IFRAME_HEIGHT));
        }
      }

      if (data?.offset !== undefined) {
        const nextOffset = Number(data.offset);
        if (Number.isFinite(nextOffset)) {
          const iframeTop = iframeRef.current.getBoundingClientRect().top + window.scrollY;
          window.scrollTo(0, nextOffset + iframeTop);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  return (
    <div className="unit-iframe__wrapper">
      <iframe
        id="unit-iframe"
        ref={iframeRef}
        title={intl.formatMessage(messages.xblockIframeTitle)}
        name="xblock-iframe"
        src={iframeUrl}
        frameBorder="0"
        allow={IFRAME_FEATURE_POLICY}
        allowFullScreen
        loading="lazy"
        style={{ height: iframeHeight }}
        scrolling="no"
        referrerPolicy="origin"
        className="xblock-container-iframe"
        aria-label={intl.formatMessage(messages.xblockIframeLabel, { xblockCount })}
      />
    </div>
  );
};

XBlockContainerIframe.propTypes = {
  blockId: PropTypes.string.isRequired,
  xblockCount: PropTypes.number.isRequired,
};

export default XBlockContainerIframe;
